import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import useCrud from 'hooks/useCrud';
import PolicyForm from '.';
import { useCallback, useEffect, useRef } from 'react';
import { createOrUpdateKnowledgePolicy, getKnowledgePolicy } from '@knowledge/service/api/activity';
import { LoadingOutlined } from '@ant-design/icons';
import { Button, Divider, Modal, message } from 'antd';
import {
  FV,
  createDefaultContexts,
  getActivityMustacheTranslate,
  getContextsRegions,
  getPlatformsByStores,
  getRegionsByStores,
  patchContextsFormRemainRegions,
  useGetNewScenario,
} from './share';
import StatusButton from 'components/StatusButton';
import { useTranslate } from '@tolgee/react';
import RegionSelect from './RegionSelect';
import { FormikConfig } from 'formik';
import { Action, Typing } from 'views/BootGuide/views/Activity';
import ScrollTo from 'views/BootGuide/components/ScrollTo';
import { DisabledProps } from 'views/Aide/Scenario/TaskForm/share';
import Empty from 'components/Empty';
import posthog from '@leyan/lytics';
import styled from '@emotion/styled';
import isEqual from 'react-fast-compare';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { useScenarioConfigContext } from '../ScenarioConfigContext';

export interface PolicyCardProps {
  autoTranslate: boolean;
  activity: Knowledge.Activity.Detail;
  regenerate: number;
  onRegenerated: () => void;
}

interface State {
  step: number;
  region: General.UpperCaseRegion;
  remainRegions: General.UpperCaseRegion[];
  activeRegion: General.UpperCaseRegion;
  initialValues: FV;
}

const STEP = {
  initial: 0,
  firstTipsFinished: 1,
  templateContentConfirmed: 2,
  secondTipsFinished: 3,
  editMode: 4,
};

function PolicyCard(props: PolicyCardProps & DisabledProps) {
  const { disabled, autoTranslate, activity, regenerate, onRegenerated } = props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const getNewScenario = useGetNewScenario();

  const { scenarioConfigs } = useScenarioConfigContext();

  const { item, loading, fetch, setItem } = useCrud({
    read: useCallback(
      ({
        activity,
        regenerate,
      }: {
        activity: Knowledge.Activity.Detail;
        regenerate: number;
      }): Promise<State> => {
        const { id, stores } = activity;

        if (regenerate) {
          const platforms = getPlatformsByStores(stores);

          const regions = getRegionsByStores(stores);

          const [firstRegion, ...remainRegions] = regions;

          return getNewScenario(activity).then((newScenario) => ({
            step: STEP.initial,
            region: firstRegion,
            remainRegions: remainRegions,
            activeRegion: remainRegions[0],
            initialValues: {
              repurchasePeriodDays: newScenario ? 30 : undefined,
              contexts: createDefaultContexts(
                platforms,
                [firstRegion],
                activity,
                scenarioConfigs,
                newScenario,
              ),
            },
          }));
        }

        return getKnowledgePolicy(id).then(
          ({ knowledgeScenarioPolicyList, repurchasePeriodDays }) => {
            const regions = getContextsRegions(knowledgeScenarioPolicyList);

            const [firstRegion, ...remainRegions] = regions;

            return {
              step: STEP.editMode,
              region: firstRegion,
              remainRegions: remainRegions,
              activeRegion: remainRegions[0],
              initialValues: {
                repurchasePeriodDays,
                contexts: knowledgeScenarioPolicyList,
              },
            };
          },
        );
      },
      [getNewScenario, scenarioConfigs],
    ),
  });

  const refsCurrent = {
    regenerate,
    activity,
    fetch,
    setItem,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    refs.current.fetch({ activity: refs.current.activity, regenerate }).catch(() => {});

    return () => {
      refs.current.setItem(undefined);
    };
  }, [regenerate]);

  if (!item) {
    return <div>{loading && <LoadingOutlined />}</div>;
  }

  const updateItem = (partial: Partial<State>) => setItem({ ...item, ...partial });

  const updateStep = (next?: any) =>
    setItem({ ...item, step: typeof next === 'number' ? next : step + 1 });

  const { step, region, remainRegions, activeRegion, initialValues } = item;

  const onSubmit: FormikConfig<FV>['onSubmit'] = ({ contexts, repurchasePeriodDays }, helpers) => {
    if (!activity) {
      return;
    }

    return createOrUpdateKnowledgePolicy({
      knowledgeId: activity.id,
      knowledgeType: 'ACTIVITY',
      activityName: activity.activityName,
      storeIds: activity.stores.map((_) => _.storeId),
      repurchasePeriodDays,
      knowledgeScenarioPolicyList: contexts,
    })
      .then(() => {
        onRegenerated();

        message.success(t('operation.saveSucceed'));

        helpers.resetForm();
      })
      .catch((knowledgeError) => {
        message.error(
          describeKnowledgeApiError(knowledgeError, undefined, t('operation.saveFailed')),
        );
      });
  };

  if (initialValues.contexts.length === 0) {
    return <PolicyEmpty />;
  }

  return (
    <PolicyForm
      activity={activity}
      autoTranslate={autoTranslate}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {(policysRender, formProps) => {
        const { values, isValid, dirty, isSubmitting, handleSubmit } = formProps;

        const { contexts } = values;

        const confirm = (
          <Action>
            <Button
              type="primary"
              disabled={disabled || !isValid || (step === STEP.editMode && !dirty)}
              loading={isSubmitting}
              onClick={() => {
                handleSubmit();
                posthog.capture('$confirmActivityContent');
                activity.platformGroupByKey && posthog.capture('$confirmActivityContentPlatform');
              }}
            >
              {t('confirm_content_btn')}
            </Button>
          </Action>
        );

        const patchRemainRegions = () =>
          patchContextsFormRemainRegions(contexts, remainRegions, activity)
            .then((contexts) => {
              updateItem({
                step: STEP.templateContentConfirmed,
                initialValues: { contexts },
              });
            })
            .catch((knowledgeError) => {
              message.error(
                describeKnowledgeApiError(knowledgeError, undefined, t('operation.saveFailed')),
              );
            });

        const hasRemainRegions = remainRegions.length > 0;

        if (step < STEP.templateContentConfirmed || !hasRemainRegions) {
          return (
            <Container>
              <div>{policysRender(region, true, disabled)}</div>
              <footer>
                {!hasRemainRegions ? (
                  confirm
                ) : (
                  <Action>
                    <StatusButton
                      type="primary"
                      disabled={!isValid || !dirty || disabled}
                      onClick={patchRemainRegions}
                    >
                      {t('confirm_content_btn')}
                    </StatusButton>
                  </Action>
                )}
              </footer>
            </Container>
          );
        }

        return (
          <Container>
            <div>
              {policysRender(
                region,
                true,
                disabled,
                ({ value, scenarioType, activitySendPoint, region, platformPolicy }) => {
                  const { platforms, itemHighlight } = platformPolicy;

                  const isTargetPlatformPolicy = (
                    platformPolicy: Knowledge.Policy.PlatformPolicy,
                  ) =>
                    platformPolicy.itemHighlight === itemHighlight &&
                    isEqual(platformPolicy.platforms, platforms);

                  type RegionValueMap = Partial<Record<General.UpperCaseRegion, string>>;

                  const getNextContexts = (regionValueMap: RegionValueMap) =>
                    contexts.map((context) => {
                      if (
                        context.scenarioType !== scenarioType ||
                        context.activitySendPoint !== activitySendPoint
                      ) {
                        return context;
                      }

                      return {
                        ...context,
                        regionPolicyList: context.regionPolicyList.map((regionPolicy) => {
                          const { [regionPolicy.region]: value } = regionValueMap;

                          if (!value) {
                            return regionPolicy;
                          }

                          return {
                            ...regionPolicy,
                            platformPolicyList: regionPolicy.platformPolicyList.map(
                              (platformPolicy) => {
                                if (!isTargetPlatformPolicy(platformPolicy)) {
                                  return platformPolicy;
                                }

                                return {
                                  ...platformPolicy,
                                  executeContext: {
                                    content: value,
                                    translatedContent: '',
                                  },
                                };
                              },
                            ),
                          };
                        }),
                      };
                    });

                  if (remainRegions.length === 0) {
                    updateItem({
                      initialValues: { contexts: getNextContexts({ [region]: value }) },
                    });
                    return Promise.resolve();
                  }

                  return new Promise<void>((resolve) => {
                    Modal.confirm({
                      title: t('aide.提示'),
                      content: t('re_gen_tip', {
                        region: getRegionLabel(region),
                        moreRegion: remainRegions.map((region) => getRegionLabel(region)).join('&'),
                      }),
                      cancelText: t('no_need_reg'),
                      okText: t('need_reg'),
                      onCancel: () => {
                        updateItem({
                          initialValues: { contexts: getNextContexts({ [region]: value }) },
                        });
                        resolve();
                      },
                      onOk: () => {
                        return remainRegions
                          .reduce<Promise<RegionValueMap>>((res, region) => {
                            return res.then((res) => {
                              const target = contexts
                                .find(
                                  (context) =>
                                    context.scenarioType === scenarioType &&
                                    context.activitySendPoint === activitySendPoint,
                                )
                                ?.regionPolicyList.find(
                                  (regionPolicy) => regionPolicy.region === region,
                                )
                                ?.platformPolicyList.find(isTargetPlatformPolicy);
                              return getActivityMustacheTranslate(
                                value,
                                region,
                                activity,
                                scenarioType,
                                platforms,
                                activitySendPoint,
                                target?.applyItems,
                              ).then((mustache) => {
                                res[region] = mustache;
                                return res;
                              });
                            });
                          }, Promise.resolve({ [region]: value }))
                          .then((regionValueMap) => {
                            updateItem({
                              initialValues: { contexts: getNextContexts(regionValueMap) },
                            });
                            resolve();
                          });
                      },
                    });
                  });
                },
              )}
              <Divider />
              {step === STEP.secondTipsFinished && <ScrollTo />}
              <Typing typing={step <= STEP.templateContentConfirmed} onFinished={updateStep}>
                {remainRegions.length > 1
                  ? t('more_region_act_tip', {
                      n: remainRegions.length,
                      region: getRegionLabel(remainRegions[0]),
                      originRegion: getRegionLabel(region),
                    })
                  : t('1_region_act_tip', {
                      region: getRegionLabel(remainRegions[0]),
                      originRegion: getRegionLabel(region),
                    })}
              </Typing>
              {step >= STEP.secondTipsFinished && [
                remainRegions.length > 1 && activeRegion && (
                  <RegionSelect
                    regions={remainRegions}
                    activeRegion={activeRegion}
                    onActiveRegionChange={(activeRegion) => updateItem({ activeRegion })}
                  />
                ),
                policysRender(activeRegion, true, disabled),
              ]}
            </div>
            <footer>{confirm}</footer>
          </Container>
        );
      }}
    </PolicyForm>
  );
}

export function PolicyEmpty() {
  const { t } = useTranslate();

  return (
    <div style={{ padding: '12px 0 60px', background: 'var(--fett-bg-panel)' }}>
      <Empty
        className="!mt-[60px]"
        description={
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {t(
              '选择活动类型后，小乐会向您了解活动的具体信息\n并通过大模型找到触达时机，生成专属销售方案',
            )}
          </div>
        }
      />
    </div>
  );
}

const Container = styled.div`
  > div {
    padding: 24px;
    background: var(--fett-bg-panel);
  }
  > footer {
    position: sticky;
    bottom: -12px;
    padding: 0 24px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    background: var(--fett-bg-panel);
    border-top: solid 1px var(--fett-border-default);
  }
`;

export default PolicyCard;
